import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger
} from "@/components/ui/popover";
import { CATEGORIES, ZONES } from "@/lib/constants";
import { isNew } from "@/lib/dates";
import { Search, MapPin, Pill, Truck, SlidersHorizontal, Sparkles, X, ArrowDownUp, Target } from "lucide-react";
import FavoriteButton from "@/components/FavoriteButton";

export default function MarketplacePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("todos");
  const [zone, setZone] = useState("todas");
  const [search, setSearch] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [appliedMin, setAppliedMin] = useState("");
  const [appliedMax, setAppliedMax] = useState("");
  const [sort, setSort] = useState("recent");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const pageSize = 24;

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = { sort, page, page_size: pageSize };
      if (category && category !== "todos") params.category = category;
      if (zone && zone !== "todas") params.zone = zone;
      if (search.trim()) params.search = search.trim();
      if (appliedMin !== "" && !isNaN(parseFloat(appliedMin))) params.min_price = parseFloat(appliedMin);
      if (appliedMax !== "" && !isNaN(parseFloat(appliedMax))) params.max_price = parseFloat(appliedMax);
      const { data } = await api.get("/listings", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setPage(1); /* eslint-disable-next-line */ }, [category, zone, search, appliedMin, appliedMax]);
  useEffect(() => { fetchItems(); /* eslint-disable-next-line */ }, [category, zone, appliedMin, appliedMax, sort, page]);
  useEffect(() => {
    const t = setTimeout(fetchItems, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const applyPrice = () => {
    setAppliedMin(minPrice);
    setAppliedMax(maxPrice);
  };
  const clearPrice = () => {
    setMinPrice(""); setMaxPrice("");
    setAppliedMin(""); setAppliedMax("");
  };

  const priceActive = appliedMin !== "" || appliedMax !== "";
  const priceLabel = priceActive
    ? `$${appliedMin || "0"} – $${appliedMax || "∞"}`
    : "Precio";

  return (
    <div className="bg-slate-50 min-h-[80vh]">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-blue-950">Medicamentos disponibles</h1>
          <p className="mt-2 text-slate-600 text-base">Filtra por categoría, zona y precio. Da clic para ver fotos y contactar por WhatsApp.</p>
        </div>

        {/* Demand-side CTA */}
        <Link to="/buscar-medicamento" className="block mb-5" data-testid="marketplace-request-cta">
          <Card className="p-4 rounded-2xl border border-amber-200 bg-amber-50 hover:bg-amber-100 transition-colors flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-amber-200 grid place-items-center shrink-0">
              <Target className="w-5 h-5 text-amber-800" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-semibold text-sm sm:text-base text-amber-900">¿No encuentras lo que buscas?</div>
              <div className="text-xs sm:text-sm text-amber-800">Publica tu búsqueda y te avisamos cuando alguien lo ofrezca.</div>
            </div>
            <span className="text-amber-900 text-sm font-medium hidden sm:inline">Publicar →</span>
          </Card>
        </Link>

        <Card className="p-4 sm:p-5 rounded-2xl border border-slate-200 bg-white">
          <div className="grid sm:grid-cols-4 gap-3">
            <div className="sm:col-span-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Buscar medicamento…" className="h-12 pl-9 bg-slate-50" data-testid="marketplace-search-input" />
              </div>
            </div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-12 bg-slate-50" data-testid="marketplace-category-select"><SelectValue placeholder="Categoría" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todas las categorías</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={zone} onValueChange={setZone}>
              <SelectTrigger className="h-12 bg-slate-50" data-testid="marketplace-zone-select"><SelectValue placeholder="Zona" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas las zonas</SelectItem>
                {ZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={`h-12 justify-start bg-slate-50 ${priceActive ? "border-blue-500 text-blue-700 ring-1 ring-blue-200" : ""}`}
                  data-testid="marketplace-price-trigger"
                >
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  {priceLabel}
                  {priceActive && <span className="ml-auto text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">activo</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-4" data-testid="marketplace-price-popover">
                <div className="text-sm font-medium text-slate-800 mb-3">Rango de precio (MXN)</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-slate-500">Mínimo</label>
                    <Input type="number" min={0} value={minPrice} onChange={(e)=>setMinPrice(e.target.value)} placeholder="$0" className="h-11 mt-1 bg-slate-50" data-testid="marketplace-price-min" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Máximo</label>
                    <Input type="number" min={0} value={maxPrice} onChange={(e)=>setMaxPrice(e.target.value)} placeholder="Sin límite" className="h-11 mt-1 bg-slate-50" data-testid="marketplace-price-max" />
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={clearPrice} data-testid="marketplace-price-clear">
                    <X className="w-4 h-4 mr-1" /> Limpiar
                  </Button>
                  <Button className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={applyPrice} data-testid="marketplace-price-apply">
                    Aplicar
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <div className="mt-3 flex items-center justify-between gap-3">
            <div className="text-sm text-slate-500">
              {!loading && <span data-testid="marketplace-count">{total} resultado{total !== 1 ? "s" : ""}</span>}
            </div>
            <div className="flex items-center gap-2">
              <ArrowDownUp className="w-4 h-4 text-slate-400" />
              <Select value={sort} onValueChange={(v) => { setSort(v); setPage(1); }}>
                <SelectTrigger className="h-10 w-44 bg-slate-50" data-testid="marketplace-sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Más recientes</SelectItem>
                  <SelectItem value="price_asc">Precio: menor a mayor</SelectItem>
                  <SelectItem value="price_desc">Precio: mayor a menor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        <div className="mt-6">
          {loading ? (
            <div className="text-center text-slate-500 py-12">Cargando…</div>
          ) : items.length === 0 ? (
            <Card className="p-10 rounded-2xl border border-dashed border-slate-300 text-center" data-testid="marketplace-empty">
              <Pill className="w-10 h-10 text-blue-300 mx-auto mb-3" />
              <p className="text-slate-700 font-medium">No hay medicamentos que coincidan</p>
              <p className="text-sm text-slate-500 mt-1">¿No lo encuentras? Publica tu búsqueda y la comunidad te ayudará.</p>
              <Link to="/buscar-medicamento" className="inline-block mt-4">
                <Button className="bg-blue-600 hover:bg-blue-700 h-12 rounded-xl" data-testid="marketplace-empty-cta">
                  <Target className="w-4 h-4 mr-2" /> Busco este medicamento
                </Button>
              </Link>
            </Card>
          ) : (
            <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="marketplace-grid">
              {items.map((it) => (
                <Link key={it.id} to={`/medicamento/${it.id}`} className="group" data-testid={`listing-card-${it.id}`}>
                  <Card className="rounded-2xl overflow-hidden border border-slate-200 bg-white hover:shadow-lg transition-shadow relative">
                    {isNew(it.created_at) && (
                      <span
                        className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-600 text-white text-[11px] font-bold shadow-md"
                        data-testid={`new-badge-${it.id}`}
                      >
                        <Sparkles className="w-3 h-3" /> NUEVO
                      </span>
                    )}
                    <div className="absolute top-3 right-3 z-10">
                      <FavoriteButton listingId={it.id} />
                    </div>
                    <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
                      <img src={it.photo_front} alt={it.medicine_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-display font-semibold text-base text-slate-900 line-clamp-2">{it.medicine_name}</h3>
                        <Badge className="bg-blue-100 text-blue-900 hover:bg-blue-100 whitespace-nowrap">{it.category}</Badge>
                      </div>
                      <p className="mt-1 text-sm text-slate-500">{it.total_units} {it.unit_label}</p>
                      <div className="mt-3 flex items-center justify-between">
                        <div>
                          <div className="text-xs text-slate-500">Costo</div>
                          <div className="text-xl font-bold text-blue-900">${it.base_cost.toFixed(0)} <span className="text-sm font-normal text-slate-500">MXN</span></div>
                        </div>
                        <div className="text-right text-xs text-slate-500">
                          <div className="flex items-center gap-1 justify-end"><MapPin className="w-3.5 h-3.5"/>{it.zone}</div>
                          {it.delivery_cost > 0 && <div className="flex items-center gap-1 justify-end mt-1"><Truck className="w-3.5 h-3.5"/>+${it.delivery_cost.toFixed(0)} envío</div>}
                        </div>
                      </div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
            {pages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-3" data-testid="marketplace-pagination">
                <Button
                  variant="outline"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  data-testid="marketplace-prev-page"
                >
                  ← Anterior
                </Button>
                <span className="text-sm text-slate-600">Página {page} de {pages}</span>
                <Button
                  variant="outline"
                  disabled={page >= pages}
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  data-testid="marketplace-next-page"
                >
                  Siguiente →
                </Button>
              </div>
            )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
